import { inject, Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent
} from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { SuraRouteManagerHelper } from '@sura-ui/atoms';
import { Router } from '@angular/router';
import { IdentityServiceImpl } from './identity.service';
import { HttpLoadingStateService } from 'src/app/infrastructure/state/http-loading-state.service';
import { AppNotificationService } from '@service/notification/app-notification.service';
import {
  conMensajeGenerico,
  MENSAJE_ERROR_COMUNICACION
} from 'src/app/common/http-error-utils';

@Injectable({ providedIn: 'root' })
export class SecurityInterceptRequestService implements HttpInterceptor {

  constructor(
    private router: SuraRouteManagerHelper,
    private injector: Injector
  ) {}
  
  redirect(url: string): void {
    window.location.href = url;
  }

  private readonly httpLoadingState = inject(HttpLoadingStateService);

  /**
   * Resolución perezosa: inyectarlo en el constructor crea un ciclo
   * HttpClient → interceptor → AppNotificationService → TranslateService →
   * TranslateLoader → HttpClient (NG0200).
   */
  private get notificationService(): AppNotificationService {
    return this.injector.get(AppNotificationService);
  }

  private get angularRouter(): Router {
    return this.injector.get(Router);
  }

  private get identityService(): IdentityServiceImpl {
    return this.injector.get(IdentityServiceImpl);
  }

  private navigateToAccessDenied(code: string | number, description: string): void {
    if (window.location.pathname.includes('/access-denied')) {
      return;
    }

    this.angularRouter.navigate(['/access-denied'], {
      queryParams: { code, description }
    });
  }

  private trySsoAuthentication(onFallback: () => void): void {
    this.identityService.getLoginForm().subscribe({
      next: (form: string) => {
        if (form && form !== 'LoggedInYet') {
          this.submitSamlForm(form);
          return;
        }
        onFallback();
      },
      error: () => onFallback()
    });
  }

  private isSamlForm(htmlContent: unknown): htmlContent is string {
    return (
      typeof htmlContent === 'string' &&
      htmlContent.toLowerCase().includes('<form')
    );
  }


  /**
   * Indica si la respuesta "200" es en realidad un fallo de parseo del cliente
   * (p. ej. el dev-server devolvió index.html en vez de JSON). En ese caso el
   * HttpErrorResponse trae status 200 pero error.error es un Error de parseo.
   */
  private isClientParseError(error: HttpErrorResponse): boolean {
    return error.error?.error instanceof Error;
  }

  /**
   * Indica si la url destino apunta a la misma página actual, evitando redirigir
   * en bucle hacia el mismo recurso.
   */
  private isSameLocation(url: string): boolean {
    try {
      return new URL(url, window.location.origin).href === window.location.href;
    } catch {
      return false;
    }
  }

  submitSamlForm(htmlContent: string): void {
    document.body.innerHTML = htmlContent;
    if (document.forms[0]) {
      document.forms[0].submit();
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.httpLoadingState.beginRequest();

    const headers = req.headers.set('X-APP-RELAYSTATE', location.href);
    const reqWithHeaders = req.clone({ headers, withCredentials: true });
    return next.handle(reqWithHeaders).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          switch (error.status) {
            case 200:
              // Redirección a IdP para autenticación.
              // Un "200" que llega como error suele ser un fallo de parseo
              // (el backend no responde y el dev-server devuelve index.html);
              // redirigir a esa misma url provocaría un bucle infinito de
              // recargas, así que en ese caso no hacemos nada.
              if (this.isClientParseError(error)) {
                break;
              }
              if (error.url?.includes('logout')) {
                this.redirect(error.error?.text || '/not-found');
                break;
              }
              if (error.url && !this.isSameLocation(error.url)) {
                this.redirect(error.url);
              }
              break;
            case 401:
              // Prioriza el formulario SAML que llega en el mismo 401.
              if (this.isSamlForm(error.error)) {
                this.submitSamlForm(error.error);
                break;
              }

              this.trySsoAuthentication(() => {
                this.navigateToAccessDenied(
                  error.type || 401,
                  'No tiene permisos para acceder a este recurso : ' + error.url
                );
              });
              break;
            case 403:
              // Usuario no autorizado
              this.navigateToAccessDenied(
                403,
                'Usuario no autorizado para acceder a este recurso'
              );
              break;
            case 0:
              // Error de red/comunicación (DNS, CORS, backend caído, timeout).
              // No es un fallo de permisos: intentar reautenticar primero y, si
              // no aplica, notificar como fallo de comunicación (no acceso denegado).
              this.trySsoAuthentication(() => {
                this.notificationService.error(
                  'notification.serviceCommunicationError',
                  true
                );
              });
              // El `message` de Angular ("Http failure response for <url>: 0 Unknown
              // Error") es técnico; se reemplaza por el texto genérico para que los
              // componentes que muestran `err.message` no expongan la URL al usuario.
              return throwError(() =>
                conMensajeGenerico(error, MENSAJE_ERROR_COMUNICACION)
              );
            case 500: {
              // Cada componente maneja su propio toast de error via catch
              break;
            }
            default: {
              break;
            }
          }
        }
        return throwError(() => error);
      }),
      finalize(() => this.httpLoadingState.endRequest())
    );
  }
}
